import { useEffect, useState } from "react";
import {
  basicBarOption,
  distributionBarOption,
  heatmapOption,
  pieOption
} from "../../constants/chart_config";
import EChartsReact from "echarts-for-react";
import { toAgeSeries, toDaySeries, toHourSeries } from "../../libs/customerDistribution";
import {
  ALL, HEATMAP_DAYS, HEATMAP_HOURS,
  heatmapFilterOptions, heatmapPeak, toHeatmapMatrix,
} from "../../libs/joinHeatmap";


const UserStatistics = ({customers, showFriendAddAndBlock = false}) => {
  const [genderData, setGenderData] = useState([]);
  /*
    연령대·가입 시간대·가입 요일은 2026-09-10 부터 **막대그래프**다(사용자 지시).
    원형 그래프는 "어느 구간이 큰가" 만 보여주고 "어느 방향으로 몰리는가" 를 못 보여준다 —
    나이·시간·요일은 순서가 있는 축이라 왼쪽부터 차례로 펼쳐야 분포로 읽힌다.

    그래서 state 모양도 `[{name, value}]`(원형용) → `{xAxis, series}`(막대용)로 바뀌었다.
    정렬·라벨 규칙은 `libs/customerDistribution.js` 한 곳에 있고 테스트로 고정돼 있다.
  */
  const [ageRangeData, setAgeRangeData] = useState({});
  const [joinHoursData, setJoinHoursData] = useState({});
  const [joinDaysData, setJoinDaysData] = useState({});
  const [friendUser, setFriendUser] = useState({});
  const [blockedUser, setBlockedUser] = useState({});
  /*
    히트맵 필터(연령대 · 성별). 서버를 다시 부르지 않는다 — `customers.joinHeatmap` 의
    칸 목록을 화면에서 걸러 합친다(`libs/joinHeatmap.js`).
  */
  const [heatAge, setHeatAge] = useState(ALL);
  const [heatGender, setHeatGender] = useState(ALL);

  useEffect(() => {
    if (customers.groupGenders) {
      setGenderChart()
      setAgesRangeChart()
      setHoursRangeChart()
      setDayOfWeekRangeChart()
      setAddFriendsRangeChart()
      setBlockedFriendsRangeChart()
    }
  }, [customers])

  const setGenderChart = () => {
    const groupGenders = customers.groupGenders
    const counts = customers.groupGenderCounts ?? {}

    setGenderData(Object.entries(groupGenders).map(([key, value]) => {
      return {
        name: key,
        value: value,
        // 툴팁·범례가 함께 보여줄 인원(2026-09-11). 없으면 종전처럼 퍼센트만 나온다.
        count: counts[key],
      }
    }))
  }

  // 🔑 서버는 세 분포를 모두 **값이 큰 순서**로 준다. 그 순서를 그대로 축에 얹으면
  //    막대그래프가 "순위표" 가 되어 버리므로 여기서 자연 순서로 다시 세운다.
  /*
    🔑 퍼센트와 **절대 인원**을 함께 세운다(2026-09-11 툴팁 요구).
       인원은 퍼센트에서 역산할 수 없다 — 분포마다 분모가 다르고 반올림도 들어가 있다.
       구 응답(인원 필드가 없던 시절)에서는 `counts` 가 null 이고 툴팁이 퍼센트만 보여준다.
  */
  const setAgesRangeChart =  () => {
    setAgeRangeData(toAgeSeries(customers.groupAges, customers.groupAgeCounts))
  }

  const setHoursRangeChart =  () => {
    setJoinHoursData(toHourSeries(customers.joinHours, customers.joinHourCounts))
  }

  const setDayOfWeekRangeChart =  () => {
    setJoinDaysData(toDaySeries(customers.joinDayOfWeeks, customers.joinDayOfWeekCounts))
  }

  const setAddFriendsRangeChart =  () => {
    const addCountGroups = customers.addCountGroups
    const xAxis = []
    const series = []

    addCountGroups.forEach(({addCount, createdAt}) => {
      xAxis.push(createdAt)
      series.push(addCount)
    })
    setFriendUser({
      xAxis: xAxis,
      series: series
    });
  }

  const setBlockedFriendsRangeChart =  () => {
    const blockedCountGroups = customers.blockedCountGroups
    const xAxis = []
    const series = []

    blockedCountGroups.forEach(({blockedCount, createdAt}) => {
      xAxis.push(createdAt)
      series.push(blockedCount)
    })
    setBlockedUser({
      xAxis: xAxis,
      series: series
    })
  }

  /*
    🔴 필터가 바뀌면 다시 계산해야 하지만, **응답이 바뀔 때 필터를 초기화하지는 않는다** —
       기간을 옮겨 가며 같은 조건(예: 20대 여성)의 패턴을 비교하는 것이 이 화면의 용도다.
       ⚠️ 대신 고른 값이 새 데이터에 없을 수 있으므로, 아래 `<select>` 는 **현재 값을 항상
       목록에 포함**시킨다(없으면 브라우저가 임의로 첫 항목을 고른 것처럼 보인다).
  */
  const heatCells = customers.joinHeatmap ?? [];
  const heatOptions = heatmapFilterOptions(heatCells);
  const heatMatrix = toHeatmapMatrix(heatCells, { age: heatAge, gender: heatGender });
  const peak = heatmapPeak(heatMatrix);

  const withCurrent = (list, current) => (list.includes(current) ? list : [...list, current]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4 relative">
        {
          showFriendAddAndBlock && (
              <>
                <div className="rounded border border-gray-300">
                  <div className="border-b border-b-gray-300 w-full p-4">
                    <h5 className="font-semibold">친구 추가 수</h5>
                  </div>
                  <div className="p-4">
                    <EChartsReact
                        option={basicBarOption(friendUser.xAxis, friendUser.series)}
                    />
                  </div>
                </div>
                <div className="bg-white rounded border border-gray-300">
                  <div className="border-b border-b-gray-300 w-full p-4">
                    <h5 className="font-semibold">친구 차단 수</h5>
                  </div>
                  <div className="p-4">
                    <EChartsReact
                        option={basicBarOption(blockedUser.xAxis, blockedUser.series)}
                    />
                  </div>
                </div>
              </>
            )
        }
        <div className="bg-white rounded border border-gray-300">
          <div className="border-b border-b-gray-300 w-full p-4">
            <h5 className="font-semibold">성별</h5>
          </div>
          <div className="p-4">
            <EChartsReact
                option={pieOption("성별", genderData, "%")}
            />
          </div>
        </div>
        {/*
          배치는 종전 그대로 **2열 그리드(반쪽 폭)** 다 — 2026-09-10 2차에 사용자가
          "그래프 배치는 기존과 동일하게, 폭은 좁게" 로 정리했다(1차에 한 줄 전체 폭으로
          넓혔던 것을 되돌린 것이다).

          🔴 반쪽 폭에서는 구간이 10~13개라 라벨이 겹치는데, **ECharts 는 겹치는 라벨을
             말없이 건너뛴다.** 그래서 `distributionBarOption` 이 `interval: 0` 으로 전부
             표시하고 45도로 눕힌다 — 그 옵션을 빼면 구간이 몇 개인지조차 알 수 없게 된다.
        */}
        <div className="bg-white rounded border border-gray-300">
          <div className="border-b border-b-gray-300 w-full p-4">
            <h5 className="font-semibold">연령대</h5>
          </div>
          <div className="p-4">
            <EChartsReact
                option={distributionBarOption(ageRangeData.xAxis, ageRangeData.series, ageRangeData.counts)}
            />
          </div>
        </div>
        <div className="bg-white rounded border border-gray-300">
          <div className="border-b border-b-gray-300 w-full p-4">
            <h5 className="font-semibold">가입 시간대</h5>
          </div>
          <div className="p-4">
            <EChartsReact
                option={distributionBarOption(joinHoursData.xAxis, joinHoursData.series, joinHoursData.counts)}
            />
          </div>
        </div>
        <div className="bg-white rounded border border-gray-300">
          <div className="border-b border-b-gray-300 w-full p-4">
            <h5 className="font-semibold">가입 요일</h5>
          </div>
          <div className="p-4">
            <EChartsReact
                option={distributionBarOption(joinDaysData.xAxis, joinDaysData.series, joinDaysData.counts)}
            />
          </div>
        </div>

        {/*
          ===== 가입 요일 x 시간대 히트맵 (2026-09-11) =====

          위 세 막대는 **주변분포**다 — "20대가 몇 %", "월요일이 몇 %" 는 알려주지만
          "20대가 월요일 몇 시에 오는가" 는 **조합해 낼 수 없다.** 그 교차를 보려고 서버가
          `joinHeatmap` 을 따로 세어 주고, 여기서 연령대·성별로 걸러 본다.

          🔑 **가로 전체**를 쓴다. 칸이 7 x 13 = 91개라 반쪽 폭에서는 한 칸이 20px 아래로 내려가
             색 차이를 읽을 수 없다(막대와 달리 이 그래프는 칸 크기가 곧 가독성이다).
          🔑 서버가 이 필드를 안 주는 구 버전에서는 **카드를 그리지 않는다** — 빈 격자만
             보여주면 "고객이 없다" 로 오해한다.
        */}
        {heatCells.length > 0 && (
          <div className="bg-white rounded border border-gray-300 col-span-2">
            <div className="border-b border-b-gray-300 w-full p-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h5 className="font-semibold">가입 요일 &times; 시간대</h5>
                {peak && (
                  <p className="text-xs text-gray-500 mt-1">
                    가장 몰린 때 <strong className="text-gray-700">{peak.day} {peak.hour}</strong>
                    {' '}· {peak.count.toLocaleString()}명 ({peak.share}%)
                    {' '}· 선택 조건 총 {heatMatrix.total.toLocaleString()}명
                  </p>
                )}
              </div>
              {/* 필터는 차트 위 한 줄에 모아 둔다 — 무엇이 걸려 있는지 그래프와 함께 보여야 한다. */}
              <div className="flex items-center gap-2 text-sm">
                <label className="text-gray-500">연령대
                  <select
                    className="ml-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    value={heatAge}
                    onChange={(e) => setHeatAge(e.target.value)}
                  >
                    {withCurrent(heatOptions.ages, heatAge).map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </label>
                <label className="text-gray-500">성별
                  <select
                    className="ml-1 border border-gray-300 rounded px-2 py-1 text-sm"
                    value={heatGender}
                    onChange={(e) => setHeatGender(e.target.value)}
                  >
                    {withCurrent(heatOptions.genders, heatGender).map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div className="p-4">
              {heatMatrix.max === 0 ? (
                // 🔑 격자를 그리지 않고 안내로 대체한다. 전부 0인 격자는 "색이 안 나온다" 는
                //    버그처럼 보인다.
                <div className="py-16 text-center text-sm text-gray-400">
                  선택한 조건(연령대 {heatAge} · 성별 {heatGender})에 해당하는 고객이 없습니다.
                </div>
              ) : (
                <EChartsReact
                    style={{ height: "320px" }}
                    option={heatmapOption(
                      HEATMAP_HOURS.map((h) => h.label),
                      HEATMAP_DAYS.map((d) => d.label),
                      heatMatrix.cells,
                      heatMatrix.max,
                    )}
                    // 🔴 필터를 바꾸면 옵션을 **덮어써야** 한다. ECharts 는 기본이 병합이라
                    //    남은 칸이 옛 색으로 남는다.
                    notMerge={true}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default UserStatistics;
